import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import moment from 'moment';
import {
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LinkIcon from '@mui/icons-material/Link';
import toast from 'react-hot-toast';
import userProgramsService from '../../../services/userProgramsService';
import { STATUS_OPTIONS } from '../constants';

const STATUS_COLOR: Record<string, string> = {
  not_started: '#6b7280',
  in_progress: '#ea580c',
  submitted: '#2563eb',
  completed: '#16a34a',
};

export interface EssayItem {
  id: string;
  title: string;
  prompt: string;
  status: string;
  targetDate: string;
  wordCount: string;
  notes: string;
  quickLink: string;
}

const createEmptyItem = (): EssayItem => ({
  id: `essay-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  title: '',
  prompt: '',
  status: 'not_started',
  targetDate: '',
  wordCount: '',
  notes: '',
  quickLink: '',
});

const labelStyle = {
  display: 'block' as const,
  color: '#111827',
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '8px',
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
};

function formatDueDate(dateStr: string): string {
  if (!dateStr || !dateStr.trim()) return '';
  const d = new Date(dateStr.trim());
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isValidUrl(url: string): boolean {
  const trimmed = url.trim();
  return trimmed.startsWith('https://') || trimmed.startsWith('http://');
}

interface EssayFormItemProps {
  item: EssayItem;
  onChange: (item: EssayItem) => void;
  onDelete: () => void;
}

const EssayFormItem: React.FC<EssayFormItemProps> = ({ item, onChange, onDelete }) => {
  const [expanded, setExpanded] = useState(true);
  const update = useCallback(
    (patch: Partial<EssayItem>) => {
      onChange({ ...item, ...patch });
    },
    [item, onChange]
  );

  const statusLabel = STATUS_OPTIONS.find((o) => o.value === item.status)?.label ?? item.status;
  const statusColor = STATUS_COLOR[item.status] ?? '#6b7280';
  const subtitleParts: string[] = [];
  if (item.wordCount?.trim()) subtitleParts.push(item.wordCount + (item.wordCount.match(/\d/) ? ' words' : ''));
  if (item.targetDate?.trim()) subtitleParts.push('Due: ' + formatDueDate(item.targetDate));
  const subtitle = subtitleParts.join(' • ');

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        marginBottom: '16px',
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Card summary phía trên */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setExpanded((x) => !x)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          backgroundColor: '#fff',
          borderBottom: expanded ? '1px solid #e5e7eb' : 'none',
          cursor: 'pointer',
        }}
      >
        <div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: subtitle ? '4px' : 0 }}>
            {(() => {
              const firstLine = item.prompt?.trim()?.split('\n')[0] || '';
              return firstLine ? (firstLine.length > 50 ? firstLine.slice(0, 50) + '...' : firstLine) : 'Essay';
            })()}
          </div>
          {subtitle && (
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: statusColor }}>
            {statusLabel}
          </span>
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ color: '#6b7280', fontSize: '20px' }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: '#6b7280', fontSize: '20px' }} />
          )}
        </div>
      </div>

      {/* Form: chỉ hiện khi expanded */}
      {expanded && (
        <div style={{ padding: '20px' }}>
          {/* Essay Prompt: text area */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Essay Prompt</label>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={item.prompt}
              onChange={(e) => update({ prompt: e.target.value })}
              placeholder="Describe yourself and how this program would align with your goals."
              variant="outlined"
              hiddenLabel
              sx={inputSx}
            />
          </div>

          {/* Target Date | Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Target Date</label>
              <DatePicker
                value={item.targetDate?.trim() && moment(item.targetDate).isValid() ? moment(item.targetDate) : null}
                onChange={(val) => update({ targetDate: val && moment(val).isValid() ? moment(val).format('YYYY-MM-DD') : '' })}
                inputFormat="MM/DD/yyyy"
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" placeholder="MM/DD/yyyy" sx={inputSx} />
                )}
              />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <FormControl fullWidth size="small">
                <Select
                  value={item.status}
                  onChange={(e: SelectChangeEvent<string>) => update({ status: e.target.value })}
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{ borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }}
                  variant="outlined"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          {/* Word Count */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Word Count</label>
            <TextField
              fullWidth
              size="small"
              value={item.wordCount}
              onChange={(e) => update({ wordCount: e.target.value })}
              placeholder="e.g. 500-650"
              variant="outlined"
              hiddenLabel
              sx={inputSx}
            />
          </div>

          {/* Quick Link & Notes */}
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Quick Link & Notes</label>
            <TextField
              fullWidth
              size="small"
              value={item.quickLink}
              onChange={(e) => update({ quickLink: e.target.value })}
              placeholder="Add link..."
              variant="outlined"
              hiddenLabel
              error={!!item.quickLink?.trim() && !isValidUrl(item.quickLink)}
              helperText={
                item.quickLink?.trim() && !isValidUrl(item.quickLink)
                  ? 'Link must start with https:// or http://'
                  : undefined
              }
              sx={{ ...inputSx, mb: 1 }}
            />
            {item.quickLink?.trim() && isValidUrl(item.quickLink) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                <a
                  href={item.quickLink.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'none',
                  }}
                >
                  <LinkIcon sx={{ fontSize: '18px' }} /> View Link
                </a>
              </div>
            )}
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={item.notes}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Add notes..."
              variant="outlined"
              hiddenLabel
              sx={inputSx}
            />
          </div>

          {/* Delete */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button
              startIcon={<DeleteOutlineIcon />}
              onClick={onDelete}
              sx={{
                color: '#dc2626',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface EssaysDropdownProps {
  onClose?: () => void;
  onSubmit?: (items: EssayItem[]) => void;
  initialItems?: EssayItem[];
  addEssayTrigger?: number;
  programManagerId?: number;
  /** Master deadline từ Application Form; dùng làm default cho targetDate khi rỗng */
  applicationDeadline?: string;
}

const EssaysDropdown: React.FC<EssaysDropdownProps> = ({
  onClose,
  onSubmit,
  initialItems,
  addEssayTrigger = 0,
  programManagerId,
  applicationDeadline = '',
}) => {
  const [items, setItems] = useState<EssayItem[]>(() =>
    initialItems?.length ? initialItems.map((it) => ({ ...createEmptyItem(), ...it })) : []
  );
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const prevTriggerRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const programManagerIdRef = useRef(programManagerId);
  programManagerIdRef.current = programManagerId;
  const isFirstRender = useRef(true);

  const debouncedAutoSave = useMemo(
    () =>
      debounce(() => {
        const id = programManagerIdRef.current;
        if (id == null) return;
        const current = itemsRef.current;
        if (current.some((it) => it.targetDate?.trim() && isNaN(new Date(it.targetDate).getTime()))) return;
        if (current.some((it) => it.quickLink?.trim() && !isValidUrl(it.quickLink))) return;
        const essays = current.map((it) => ({
          title: it.title || '',
          prompt: it.prompt || undefined,
          status: it.status || 'not_started',
          targetDate: it.targetDate || '',
          wordCount: it.wordCount || undefined,
          notes: it.notes || undefined,
          link: it.quickLink || undefined,
        }));
        userProgramsService
          .patchProgramsManager(id, { essays })
          .then(() => {
            setAutoSaved(true);
            toast.success(current.length === 1 ? '1 essay saved.' : `${current.length} essays saved.`);
          })
          .catch(() => {});
      }, 800),
    []
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setAutoSaved(false);
    debouncedAutoSave();
    return () => debouncedAutoSave.cancel();
  }, [items, debouncedAutoSave]);

  const createItemWithDeadline = useCallback(() => {
    const item = createEmptyItem();
    if (applicationDeadline?.trim()) item.targetDate = applicationDeadline.trim();
    return item;
  }, [applicationDeadline]);

  useEffect(() => {
    if (addEssayTrigger > prevTriggerRef.current) {
      const count = addEssayTrigger - prevTriggerRef.current;
      setItems((prev) => [...prev, ...Array.from({ length: count }, createItemWithDeadline)]);
      prevTriggerRef.current = addEssayTrigger;
    }
  }, [addEssayTrigger, createItemWithDeadline]);

  const handleChange = useCallback((index: number, updated: EssayItem) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
  }, []);

  const handleDelete = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    const invalidDateIndex = items.findIndex(
      (it) => it.targetDate.trim() && isNaN(new Date(it.targetDate.trim()).getTime())
    );
    if (invalidDateIndex !== -1) {
      toast.error(`Please enter a valid Target Date in item ${invalidDateIndex + 1}.`);
      return;
    }

    const invalidLinkIndex = items.findIndex(
      (it) => it.quickLink?.trim() && !isValidUrl(it.quickLink)
    );
    if (invalidLinkIndex !== -1) {
      toast.error(`Please enter a valid link (must start with https:// or http://) in item ${invalidLinkIndex + 1}.`);
      return;
    }

    const essays = items.map((it) => ({
      title: it.title || '',
      prompt: it.prompt || undefined,
      status: it.status || 'not_started',
      targetDate: it.targetDate || '',
      wordCount: it.wordCount || undefined,
      notes: it.notes || undefined,
      link: it.quickLink || undefined,
    }));

    if (programManagerId != null) {
      setSaving(true);
      try {
        await userProgramsService.patchProgramsManager(programManagerId, {
          essays,
        });
        const count = items.length;
        toast.success(count === 1 ? '1 essay saved.' : `${count} essays saved.`);
        onSubmit?.(items);
        // Không gọi onClose — giữ form hiển thị sau khi save
      } catch {
        // Error toast handled by http interceptor
      } finally {
        setSaving(false);
      }
    } else {
      onSubmit?.(items);
      onClose?.();
    }
  }, [items, programManagerId, onSubmit, onClose]);

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        marginTop: '-12px',
        marginBottom: '12px',
      }}
    >
      {items.length === 0 ? (
        <div style={{ color: '#6b7280', fontSize: '14px', padding: '16px 0', textAlign: 'center' }}>
          Click &quot;+ Add Essay&quot; above to add an essay.
        </div>
      ) : (
        items.map((item, index) => (
          <EssayFormItem
            key={item.id}
            item={item}
            onChange={(updated) => handleChange(index, updated)}
            onDelete={() => handleDelete(index)}
          />
        ))
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
        {programManagerId != null && autoSaved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CheckCircleIcon sx={{ fontSize: '18px', color: '#16a34a' }} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Auto-saved</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
          <Button onClick={onClose} sx={{ color: '#6b7280', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{ bgcolor: '#2563eb', fontWeight: 600 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EssaysDropdown;
